import { LOG_BUYER_REPLY } from "@lib/graphql/outreach";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useMutation } from "urql";

interface Props {
  open: boolean;
  buyerId: string;
  onClose: () => void;
  onLogged?: () => void;
}

export function ReplyModal({ open, buyerId, onClose, onLogged }: Props) {
  const [, logReply] = useMutation(LOG_BUYER_REPLY);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const result = await logReply({ input: { id: buyerId, replyText: text } });
    setBusy(false);
    if (result.data?.logBuyerReply?.errors?.length === 0) {
      onLogged?.();
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reply-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close reply modal"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="reply-modal-title" className="text-lg font-semibold text-text-primary">
              Log reply
            </h3>
            <p className="mt-0.5 text-xs text-text-muted">
              Paste the buyer's reply from LinkedIn. Marks them as replied.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-text-muted hover:bg-card-hover hover:text-text-primary"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste reply text here…"
          rows={6}
          className="mt-3 block w-full rounded-md border border-input-border bg-card-alt px-3 py-2 text-sm text-text-primary"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-input-border px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy || !text.trim()}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            Log reply
          </button>
        </div>
      </div>
    </div>
  );
}
